import './styles.scss'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import InfiniteScroll from 'react-infinite-scroll-component'
import { Galleria } from 'primereact/galleria'
import { Skeleton } from 'primereact/skeleton'
import apiAdapterCoffeeWeb from 'Services/apiAdapter_CoffeeWeb'
import { NoData } from 'Assets/Images'
import { getLocalUserDetails } from 'Utils/LocalStorage_Handler'
import CreatePostModal from './Components/CreatePostModal'
import CoffeeCommunities from '../CoffeeCommunities'

const CoffeePostScreen = (props) => {
  const { showModal, setShowModal, searchTextField, containerRef, hasHeader, onOrganizationStatusChange } = props
  const { t } = useTranslation()
  const pageSize = 10
  const MAX_DESCRIPTION_LENGTH = 120

  const [mediaList, setMediaList] = useState([])
  const [posts, setPosts] = useState([])
  const [selectedCommunityId, setSelectedCommunityId] = useState(null)
  const [organizationStatus, setOrganizationStatus] = useState(null)
  const [loadingPosts, setLoadingPosts] = useState(true)
  const [loadingMorePosts, setLoadingMorePosts] = useState(false)
  const [hasMore, setHasMore] = useState(true)
  const [pageNumber, setPageNumber] = useState(1)
  const [expandedPosts, setExpandedPosts] = useState(new Set())
  const userDetails = getLocalUserDetails()

  useEffect(() => {
    fetchAllMediaPosts(true)
  }, [])

  const fetchAllMediaPosts = async (isInitialLoad = false) => {
    const userId = userDetails?.id || 0

    if (isInitialLoad) {
      setLoadingPosts(true)
      setPageNumber(1)
      setHasMore(true)
    } else {
      setLoadingMorePosts(true)
    }

    try {
      const currentPage = isInitialLoad ? 1 : pageNumber
      const response = await apiAdapterCoffeeWeb.getPostsWithPagination({ pageNumber: currentPage, pageSize, userId })

      const responseData = response?.data?.items || []
      const organizationStatus = response?.data?.organization_status

      setMediaList(response?.data)
      setOrganizationStatus(organizationStatus)
      onOrganizationStatusChange(organizationStatus)
      if (isInitialLoad) {
        setPosts(responseData)
      } else {
        setPosts((prev) => [...prev, ...responseData])
      }

      if (responseData.length < pageSize) {
        setHasMore(false)
      } else {
        setPageNumber((prev) => prev + 1)
      }
    } catch (error) {
      setHasMore(false)
    } finally {
      setLoadingPosts(false)
      setLoadingMorePosts(false)
    }
  }

  const loadMorePosts = () => {
    if (!loadingMorePosts && hasMore) {
      fetchAllMediaPosts(false)
    }
  }

  const itemTemplate = (item) => <>{item?.mediatype?.startsWith('video') ? <video controls src={item?.mediaurl} alt={item?.medianame} controlsList="nodownload noplaybackrate" disablePictureInPicture disableRemotePlayback /> : <img src={item?.mediaurl} alt={item?.medianame} />}</>

  const thumbnailTemplate = (item) => <img src={item?.mediaurl} alt={item?.medianame} />

  const closePostModal = () => {
    setShowModal(false)
  }

  const filteredPosts = posts?.filter((post) => post?.channelName?.toLowerCase().includes(searchTextField.toLowerCase()))

  const getTruncatedText = (text, maxLength) => {
    if (text.length <= maxLength) return text

    return `${text.substring(0, maxLength).trim()}`
  }

  const togglePostExpansion = (postId) => {
    setExpandedPosts((prev) => {
      const newSet = new Set(prev)

      if (newSet.has(postId)) {
        newSet.delete(postId)
      } else {
        newSet.add(postId)
      }

      return newSet
    })
  }

  const handleCreatePost = (post) => {
    setPosts((prev) => [post, ...prev])
    setShowModal(false)
  }

  const renderPostText = (post) => {
    const postId = post?._id || post?.channelName
    const description = post?.description?.trim() || ''
    const hasDescription = description?.length > 0
    const hasLink = Boolean(post?.channelLink)
    const isExpanded = expandedPosts.has(postId)
    const isLongText = description.length > MAX_DESCRIPTION_LENGTH

    if (!hasDescription && hasLink) {
      return (
        <div className="post-text-container">
          <p className="post-text">
            <span className="link-title">{t('LINK')}:</span>
            <a href={post.channelLink} target="_blank" rel="noreferrer" className="post-link-inline">
              {post?.channelLink}
            </a>
          </p>
        </div>
      )
    }

    let displayText = description

    if (!isExpanded && isLongText) {
      displayText = getTruncatedText(description, MAX_DESCRIPTION_LENGTH)
    }

    return (
      <div className="post-text-container">
        <p className="post-text">
          {displayText}

          {!isExpanded && (isLongText || hasLink) && (
            <>
              <span className="read-more-dots">...</span>
              <button className="read-more-btn" onClick={() => togglePostExpansion(postId)}>
                {t('READ_MORE') || 'Read More'}
              </button>
            </>
          )}

          {isExpanded && (
            <>
              {hasLink && (
                <>
                  <br />
                  <span className="link-title">{t('LINK')}:</span>
                  <a href={post.channelLink} target="_blank" rel="noreferrer" className="post-link-inline">
                    {post.channelLink}
                  </a>
                </>
              )}

              <>
                <span className="read-more-dots">...</span>
                <button className="read-less-btn" onClick={() => togglePostExpansion(postId)}>
                  {t('READ_LESS') || 'Read Less'}
                </button>
              </>
            </>
          )}
        </p>
      </div>
    )
  }

  const PostSkeleton = () => (
    <div className="post-skeleton-card">
      {/* Header */}
      <div className="post-skeleton-header">
        <Skeleton shape="circle" size="60px" className="post-skeleton-avatar" />
        <div className="post-skeleton-meta">
          <Skeleton width="150px" height="28px" className="post-skeleton-author" />
        </div>
      </div>

      {/* Media */}
      <div className="post-skeleton-media">
        <Skeleton width="100%" height="55vh" className="post-skeleton-image" />
      </div>

      {/* Description skeleton */}
      <div className="post-skeleton-text">
        <Skeleton width="100%" height="28px" className="post-skeleton-line" />
      </div>
    </div>
  )

  const renderFeedContent = () => {
    if (loadingPosts) {
      return Array?.from({ length: 3 }).map((_, i) => <PostSkeleton key={i} />)
    }

    if (posts.length === 0 && !loadingPosts) {
      return (
        <div className="no-data-wrapper">
          <img src={NoData} alt="no-data" />
        </div>
      )
    }

    return (
      <>
        <InfiniteScroll
          dataLength={posts.length}
          next={loadMorePosts}
          hasMore={hasMore}
          scrollableTarget="feed-list-container"
          loader={
            loadingMorePosts && (
              <div>
                {Array?.from({ length: 2 }).map((_, i) => (
                  <PostSkeleton key={`loading-${i}`} />
                ))}
              </div>
            )
          }
          endMessage={
            !hasMore &&
            posts.length > 0 &&
            filteredPosts.length > 0 && (
              <div className="end-of-posts-message">
                <p>{t('NO_MORE_POSTS') || 'No more posts to load'}</p>
              </div>
            )
          }
        >
          {filteredPosts?.map((post, index) => (
            <div className="post-card" key={post?._id || index}>
              <div className="post-header">
                <div className="author-info">
                  <img src={post?.channelLogo} alt="avatar-logo" />
                </div>

                <div className="post-meta">
                  <h4 className="author-name">{post?.channelName}</h4>
                  <span className="author-country">{post?.country}</span>
                </div>
              </div>

              {post?.media?.length > 0 && (
                <div className="post-image-wrapper">
                  <Galleria value={post.media} numVisible={5} circular showItemNavigators={post.media.length > 1} showItemNavigatorsOnHover={true} showIndicators={post.media.length > 1} showThumbnails={false} item={itemTemplate} thumbnail={thumbnailTemplate} />
                </div>
              )}
              {renderPostText(post)}
            </div>
          ))}
        </InfiniteScroll>

        {!loadingPosts && filteredPosts?.length === 0 && posts.length > 0 && (
          <div className="no-data-wrapper">
            <img src={NoData} alt="no-data" />
          </div>
        )}
      </>
    )
  }

  return (
    <>
      <div className="coffee-feed-wrapper">
        <div className="coffee-feed-container">
          <div ref={containerRef} id="feed-list-container" className={`feed-list ${!userDetails ? 'guest-feed-list' : ''} ${hasHeader ? 'with-header' : ''}`}>
            {renderFeedContent()}
          </div>
          <div className={`coffee-communities-wrapper ${!userDetails ? 'guest-list' : ''} ${hasHeader ? 'with-header' : ''}`}>
            <CoffeeCommunities isCoffeePost={true} selectedCommunityId={selectedCommunityId} onCommunitySelect={setSelectedCommunityId} onBackToCommunities={() => setSelectedCommunityId(null)} hasHeader={hasHeader} />
          </div>
        </div>
      </div>
      <CreatePostModal visible={showModal} closeModal={closePostModal} organizationStatus={organizationStatus} mediaList={mediaList} onOrganizationStatusChange={onOrganizationStatusChange} onPost={handleCreatePost} />
    </>
  )
}

export default CoffeePostScreen
